import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import type {SliceComponentProps} from '@prismicio/react'
import cn from 'classnames'
import {DEFAULT_PADDING, PADDING_BOTTOM_CLASS, PADDING_TOP_CLASS} from 'src/common/paddingClasses'

export type FourFeatureTilesIconsProps = SliceComponentProps<Content.FourFeatureTilesIconsSlice>

const bgMap: Record<string, string> = {
  'Dark green': 'bg-green-dark',
  'Grey/100': 'bg-moss-grey-100',
  'Moss White': 'bg-moss-white',
}

const FourFeatureTilesIcons = ({slice}: FourFeatureTilesIconsProps): JSX.Element => {
  const p = slice.primary
  const isDark = p.background_color === 'Dark green'
  const bgClass = bgMap[p.background_color ?? 'Dark green'] ?? 'bg-green-dark'
  const ptClass = PADDING_TOP_CLASS[p.padding_top ?? DEFAULT_PADDING] ?? ''
  const pbClass = PADDING_BOTTOM_CLASS[p.padding_bottom ?? DEFAULT_PADDING] ?? ''

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      data-nav-dark={isDark || undefined}
      className={cn(bgClass, ptClass, pbClass, isDark ? 'text-moss-white' : 'text-moss-black')}
    >
      <div className="mx-auto max-w-[1280px] px-5 md:px-10">
        {(p.eyebrow || p.heading) && (
          <div className="mb-12">
            {p.eyebrow && (
              <p
                className={cn(
                  'mb-4 text-[11px] font-semibold uppercase tracking-widest',
                  isDark ? 'text-moss-white/70' : 'text-black/45',
                )}
              >
                {p.eyebrow}
              </p>
            )}
            {p.heading && (
              <h4 className={cn('text-h4 md:text-h3', isDark ? 'text-moss-white' : 'text-moss-black')}>{p.heading}</h4>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-12 lg:grid-cols-4">
          {p.features.map((item, i) => (
            <div key={i}>
              {isFilled.image(item.icon) ? (
                <div
                  className={cn(
                    'mb-4 flex size-10 items-center justify-center rounded-[10px]',
                    isDark ? 'bg-moss-white/[0.06]' : 'bg-black/[0.06]',
                  )}
                >
                  <PrismicNextImage field={item.icon} className="size-5 brightness-0 invert" sizes="20px" />
                </div>
              ) : (
                <div
                  className={cn(
                    'mb-4 flex size-10 items-center justify-center rounded-[10px]',
                    isDark ? 'bg-moss-white/[0.06]' : 'bg-black/[0.06]',
                  )}
                />
              )}
              <div className="mb-2 max-w-[180px] text-22 font-medium leading-[1.3]">{item.title}</div>
              <div className={cn('text-[14px] leading-relaxed', isDark ? 'text-moss-white/65' : 'text-black/55')}>
                {item.description}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FourFeatureTilesIcons
